import React from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { AllowListTableContainer } from '@occc/feat-allow-list';
import {
  Page,
  TableContainer,
  SearchForm,
  SearchFormProps,
  TableHeader,
} from '@occc-app/design-system';
import { Tab } from '@occc/ui-design-system';
import { MembershipItemFragment } from '@occc/graphql';
import { PaginationInfo } from '@occc-app/hooks/usePaginatedQuery';
import { TabContent, Tabs } from '@occc-app/routes/Member/MemberDetailsPage/components/Tabs';
import { useTabQueryParam } from '@occc/utils-query-params';

import { MembersTableContainer } from './MembersTableContainer';
import { MembersListTab } from './types';
import { DEFAULT_MEMBERS_LIST_TAB, MEMBERS_LIST_TAB_PARAM, MEMBERS_LIST_TABS } from './constants';

const MEMBERS_PAGE_TITLE = 'Members';
const ALLOW_LIST_PAGE_TITLE = 'List of Allowed Phone Numbers';

type MemberListPageProps = {
  membersFilterText?: string;
  onMembersSearchFormSubmit: SearchFormProps['onSubmit'];
  onClickMember: (member: MembershipItemFragment) => void;
  paginationInfo: PaginationInfo;
  onPageChange: (info: PaginationInfo) => void;
  isUserPermittedToViewAllowList: boolean;
};

export const MemberListPage: React.FC<MemberListPageProps> = ({
  membersFilterText,
  onMembersSearchFormSubmit,
  onClickMember,
  paginationInfo,
  onPageChange,
  isUserPermittedToViewAllowList,
}) => {
  const [activeTab, setActiveTab] = useTabQueryParam<MembersListTab>(
    MEMBERS_LIST_TAB_PARAM,
    DEFAULT_MEMBERS_LIST_TAB,
    MEMBERS_LIST_TABS,
  );
  const handleChange = (_event: React.SyntheticEvent<Element, Event>, value: MembersListTab) => {
    setActiveTab(value);
  };
  const pageHeaderProps = {
    title: 'Member Management',
    titleTestId: 'members-title',
  };

  return (
    <Page
      browserTabTitle={activeTab === 'members' ? MEMBERS_PAGE_TITLE : ALLOW_LIST_PAGE_TITLE}
      pageHeaderProps={pageHeaderProps}
    >
      <Tabs value={activeTab} onChange={handleChange}>
        <Tab value={'members' satisfies MembersListTab} label='All Members' wrapped />
        {isUserPermittedToViewAllowList && (
          <Tab value={'allow-list' satisfies MembersListTab} label='Allow List' />
        )}
      </Tabs>

      <TabContent id={'members' satisfies MembersListTab} value={activeTab}>
        <TableContainer>
          <TableHeader
            title={MEMBERS_PAGE_TITLE}
            actions={
              <SearchForm
                onSubmit={onMembersSearchFormSubmit}
                initialValues={{ search: membersFilterText }}
              />
            }
          />

          <MembersTableContainer
            onClickMember={onClickMember}
            filterText={membersFilterText}
            paginationInfo={paginationInfo}
            onPageChange={onPageChange}
          />
        </TableContainer>
      </TabContent>

      {isUserPermittedToViewAllowList && (
        <TabContent id={'allow-list' satisfies MembersListTab} value={activeTab}>
          <AllowListTableContainer tableTitle={ALLOW_LIST_PAGE_TITLE} />
        </TabContent>
      )}
    </Page>
  );
};
